/**
 * New task (slice 2b spec §10.3): a dialog, a bottom sheet on a phone. What
 * needs doing, and what done looks like (optional); the For picker waits for
 * slice 6. Create lands on the new task's Task detail (decision 9).
 */
import { Button } from "@crew/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crew/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@crew/ui/components/field";
import { Input } from "@crew/ui/components/input";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { Textarea } from "@crew/ui/components/textarea";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAskGate } from "../setup/api";
import { AskNotes } from "../setup/ask-gate";
import { gateNotice } from "../setup/ask-notice";
import { NoticeStrip } from "@crew/ui/components/composer";
import { useCreateTask, type NewTask } from "./api";

const TITLE_MAX = 140;
const DONE_MAX = 600;
const LINE = "Goes to the Chief of Staff, who picks it up in a new thread.";

type Create = ReturnType<typeof useCreateTask>;

function NewTaskForm({
  create,
  onCreate,
  onCancel,
  mobile,
}: {
  create: Create;
  onCreate: (input: NewTask) => void;
  onCancel: () => void;
  mobile: boolean;
}) {
  const gate = useAskGate();
  const notice = gateNotice(gate);
  const [title, setTitle] = useState("");
  const [done, setDone] = useState("");
  const ready = title.trim().length > 0 && gate.open && !create.isPending;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    const doneLooksLike = done.trim();
    onCreate({ title: title.trim(), ...(doneLooksLike ? { done_looks_like: doneLooksLike } : {}) });
  };
  const Footer = mobile ? DrawerFooter : DialogFooter;
  const cancel = (
    <Button type="button" variant="ghost" onClick={onCancel} disabled={create.isPending}>
      Cancel
    </Button>
  );
  return (
    <form onSubmit={submit} className="flex flex-col gap-5" data-new-task>
      <div className={mobile ? "flex flex-col gap-5 px-4" : "flex flex-col gap-5"}>
        <Field>
          <FieldLabel htmlFor="new-task-title">What needs doing?</FieldLabel>
          <Input
            id="new-task-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={TITLE_MAX}
            placeholder="Plan next week's meals"
            autoFocus
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="new-task-done">Details</FieldLabel>
          <Textarea
            id="new-task-done"
            value={done}
            onChange={(e) => setDone(e.target.value)}
            maxLength={DONE_MAX}
            rows={3}
            placeholder="Five dinners, a shopping list, nothing over 40 minutes."
          />
          <FieldDescription>Optional. What you need from it.</FieldDescription>
        </Field>
        {notice ? <NoticeStrip notice={notice} standalone /> : null}
        <AskNotes gate={gate} error={create.error} />
      </div>
      <Footer>
        {/* The sheet stacks Create above Cancel (board 39b), in focus order too; the dialog puts Create on the right. */}
        {mobile ? null : cancel}
        <Button type="submit" disabled={!ready}>
          {create.isPending ? "Creating…" : "Create task"}
        </Button>
        {mobile ? cancel : null}
      </Footer>
    </form>
  );
}

export function NewTaskWindow({
  open,
  onOpenChange,
  areaId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Slice 6d (spec §8.2): New task on an Area's page makes it there. */
  areaId?: string;
}) {
  const mobile = useIsMobile();
  const navigate = useNavigate();
  // Here, not in the form: the window outlives the form, so the landing on
  // Task detail survives the form unmounting (Devin on #260).
  const create = useCreateTask();
  const onCreate = (input: NewTask) =>
    create.mutate(areaId ? { ...input, area_id: areaId } : input, {
      onSuccess: ({ task }) => navigate(`/tasks/${task.id}`),
    });
  // While the task is being created, nothing closes the window: it lands on the new task.
  const change = (next: boolean) => {
    if (!next && create.isPending) return;
    // A closed window forgets a failed create, so reopening starts clean (Devin on #260).
    if (!next) create.reset();
    onOpenChange(next);
  };
  const close = () => change(false);
  const form = open ? (
    <NewTaskForm create={create} onCreate={onCreate} onCancel={close} mobile={mobile} />
  ) : null;
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={change} showSwipeHandle>
        <DrawerContent
          showCloseButton={!create.isPending}
          className="pb-[env(safe-area-inset-bottom)]"
        >
          <DrawerHeader className="pb-5">
            <DrawerTitle>New task</DrawerTitle>
            <DrawerDescription>{LINE}</DrawerDescription>
          </DrawerHeader>
          {form}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="sm:max-w-[520px]" showCloseButton={!create.isPending}>
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
          <DialogDescription>{LINE}</DialogDescription>
        </DialogHeader>
        {form}
      </DialogContent>
    </Dialog>
  );
}
